import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Checkbox, Icon, Message, Header, Segment, Grid, Confirm, Label, Loader } from 'semantic-ui-react';
import { useAuth, useOrganization } from '@clerk/clerk-react';
import { useFeatureFlagsClient } from './featureFlags.client';
import { Layout } from '../components/Layout';
import { useIsSuperAdmin } from '../hooks/useIsSuperAdmin';
const FeatureFlagsPage = () => {
    const { isLoaded: authLoaded, isSignedIn } = useAuth();
    const { isLoaded: orgLoaded } = useOrganization();
    const [featureFlags, setFeatureFlags] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [formData, setFormData] = useState({ name: '', enabled: false });
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [flagToDelete, setFlagToDelete] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const featureFlagsClient = useFeatureFlagsClient();
    const isSuperAdmin = useIsSuperAdmin();
    // Show loading while Clerk is loading
    if (!authLoaded || !orgLoaded) {
        return (React.createElement(Layout, null,
            React.createElement(Segment, { style: { padding: '50px', textAlign: 'center' } },
                React.createElement(Loader, { active: true, inline: "centered" }, "Loading..."))));
    }
    // Redirect if not super admin
    if (!isSuperAdmin) {
        return (React.createElement(Layout, null,
            React.createElement(Segment, { placeholder: true, style: { margin: '50px' } },
                React.createElement(Header, { icon: true },
                    React.createElement(Icon, { name: "lock" }),
                    "Access Denied"),
                React.createElement("p", null, "This page is only accessible to super administrators."))));
    }
    useEffect(() => {
        // Only load feature flags after Clerk has loaded and user is signed in
        if (authLoaded && orgLoaded && isSignedIn) {
            loadFeatureFlags();
        }
    }, [authLoaded, orgLoaded, isSignedIn]);
    useEffect(() => {
        if (successMessage) {
            const timer = setTimeout(() => setSuccessMessage(''), 3000);
            return () => clearTimeout(timer);
        }
    }, [successMessage]);
    useEffect(() => {
        if (errorMessage) {
            const timer = setTimeout(() => setErrorMessage(''), 5000);
            return () => clearTimeout(timer);
        }
    }, [errorMessage]);
    const loadFeatureFlags = async () => {
        try {
            setLoading(true);
            const flags = await featureFlagsClient.listFeatureFlags();
            setFeatureFlags(flags);
        }
        catch (error) {
            setErrorMessage('Failed to load feature flags');
        }
        finally {
            setLoading(false);
        }
    };
    const handleCreate = async () => {
        try {
            setLoading(true);
            // Create new flag
            const newFlagData = {
                name: formData.name,
                enabled: formData.enabled
            };
            await featureFlagsClient.createFeatureFlag(newFlagData);
            setSuccessMessage('Feature flag created successfully');
            await loadFeatureFlags();
            setModalVisible(false);
            setFormData({ name: '', enabled: false });
        }
        catch (error) {
            setErrorMessage('Failed to create feature flag');
            console.error('Error creating feature flag:', error);
        }
        finally {
            setLoading(false);
        }
    };
    const handleDelete = async (flagId) => {
        try {
            setLoading(true);
            await featureFlagsClient.deleteFeatureFlag(flagId);
            setSuccessMessage('Feature flag deleted successfully');
            await loadFeatureFlags();
        }
        catch (error) {
            setErrorMessage('Failed to delete feature flag');
        }
        finally {
            setLoading(false);
            setDeleteConfirmOpen(false);
            setFlagToDelete(null);
        }
    };
    const handleQuickToggle = async (flag) => {
        try {
            const updatedFlag = await featureFlagsClient.toggleFeatureFlag(flag.id);
            setSuccessMessage(`Feature flag ${updatedFlag.enabled ? 'enabled' : 'disabled'}`);
            await loadFeatureFlags();
        }
        catch (error) {
            setErrorMessage('Failed to toggle feature flag');
        }
    };
    const handleModalCancel = () => {
        setModalVisible(false);
        setFormData({ name: '', enabled: false });
    };
    const confirmDelete = (flagId) => {
        setFlagToDelete(flagId);
        setDeleteConfirmOpen(true);
    };
    const isFormValid = formData.name.trim().length >= 3 && /^[a-zA-Z0-9_-]+$/.test(formData.name);
    return (React.createElement("div", { style: { padding: '24px' } },
        successMessage && (React.createElement(Message, { success: true, onDismiss: () => setSuccessMessage('') },
            React.createElement(Message.Header, null, "Success"),
            React.createElement("p", null, successMessage))),
        errorMessage && (React.createElement(Message, { error: true, onDismiss: () => setErrorMessage('') },
            React.createElement(Message.Header, null, "Error"),
            React.createElement("p", null, errorMessage))),
        React.createElement(Segment, null,
            React.createElement(Grid, null,
                React.createElement(Grid.Row, null,
                    React.createElement(Grid.Column, { width: 12 },
                        React.createElement(Header, { as: "h2" },
                            React.createElement(Icon, { name: "setting" }),
                            React.createElement(Header.Content, null,
                                "Feature Flags Management",
                                React.createElement(Header.Subheader, null, "Manage feature flags to control application functionality across environments.")))),
                    React.createElement(Grid.Column, { width: 4, textAlign: "right" },
                        React.createElement(Button, { primary: true, icon: "plus", content: "Create Flag", onClick: () => setModalVisible(true) })))),
            loading && React.createElement(Loader, { active: true }, "Loading feature flags..."),
            React.createElement(Table, { celled: true, striped: true },
                React.createElement(Table.Header, null,
                    React.createElement(Table.Row, null,
                        React.createElement(Table.HeaderCell, null, "Name"),
                        React.createElement(Table.HeaderCell, null, "Status"),
                        React.createElement(Table.HeaderCell, null, "Created"),
                        React.createElement(Table.HeaderCell, null, "Last Updated"),
                        React.createElement(Table.HeaderCell, null, "Actions"))),
                React.createElement(Table.Body, null, featureFlags.map((flag) => (React.createElement(Table.Row, { key: flag.id },
                    React.createElement(Table.Cell, null,
                        React.createElement(Icon, { name: "flag" }),
                        React.createElement("strong", null, flag.name)),
                    React.createElement(Table.Cell, null,
                        React.createElement("span", { style: { display: 'inline-flex' }, title: "Click to toggle" },
                            React.createElement(Label, { as: "a", color: flag.enabled ? 'green' : 'red', onClick: () => handleQuickToggle(flag), style: { cursor: 'pointer' } },
                                React.createElement(Icon, { name: flag.enabled ? 'toggle on' : 'toggle off' }),
                                flag.enabled ? 'Enabled' : 'Disabled'))),
                    React.createElement(Table.Cell, null, new Date(flag.createdAt).toLocaleDateString()),
                    React.createElement(Table.Cell, null, new Date(flag.lastUpdated).toLocaleString()),
                    React.createElement(Table.Cell, null,
                        React.createElement("span", { style: { display: 'inline-flex' }, title: "Delete" },
                            React.createElement(Button, { icon: "trash", color: "red", onClick: () => confirmDelete(flag.id) })))))))),
            featureFlags.length === 0 && !loading && (React.createElement(Segment, { placeholder: true },
                React.createElement(Header, { icon: true },
                    React.createElement(Icon, { name: "flag outline" }),
                    "No feature flags found"),
                React.createElement(Button, { primary: true, onClick: () => setModalVisible(true) }, "Create your first feature flag")))),
        React.createElement(Modal, { open: modalVisible, onClose: handleModalCancel, size: "small" },
            React.createElement(Modal.Header, null,
                React.createElement(Icon, { name: "flag" }),
                "Create Feature Flag"),
            React.createElement(Modal.Content, null,
                React.createElement(Form, null,
                    React.createElement(Form.Field, { required: true, error: formData.name && !isFormValid },
                        React.createElement("label", null, "Feature Flag Name"),
                        React.createElement(Input, { placeholder: "e.g., new-dashboard, beta-features", value: formData.name, onChange: (e) => setFormData({ ...formData, name: e.target.value }), error: formData.name && !isFormValid }),
                        formData.name && !isFormValid && (React.createElement("div", { className: "ui pointing red basic label" }, "Name must be at least 3 characters and contain only letters, numbers, underscores, and hyphens"))),
                    React.createElement(Form.Field, null,
                        React.createElement(Checkbox, { label: "Enabled", checked: formData.enabled, onChange: (e, { checked }) => setFormData({ ...formData, enabled: !!checked }) })))),
            React.createElement(Modal.Actions, null,
                React.createElement(Button, { onClick: handleModalCancel }, "Cancel"),
                React.createElement(Button, { primary: true, disabled: !isFormValid, loading: loading, onClick: handleCreate },
                    React.createElement(Icon, { name: "checkmark" }),
                    "Create"))),
        React.createElement(Confirm, { open: deleteConfirmOpen, header: "Delete Feature Flag", content: "Are you sure you want to delete this feature flag? This action cannot be undone.", onCancel: () => {
                setDeleteConfirmOpen(false);
                setFlagToDelete(null);
            }, onConfirm: () => flagToDelete && handleDelete(flagToDelete), cancelButton: "Cancel", confirmButton: "Delete" })));
};
export default FeatureFlagsPage;
